import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox, Radio } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import { Icon } from 'Components/UI';
import {
    ActivitiesOption,
    ActivitiesRadioLabel,
    ActivitiesTag,
    Body,
    Content,
    FooterButtons,
    FooterDivider,
    OrientationLabel,
    OrientationOption,
    OrientationOptions,
    Section,
    SectionLabel,
    StyledCheckbox,
    StyledRadio,
} from './styles';

export type TypePrintOrientation = 'portrait' | 'landscape';
export type TypePrintActivitiesScope = 'filtered' | 'all';

export interface IPrintDetailsSettings {
    activitiesScope: TypePrintActivitiesScope;
    orientation: TypePrintOrientation;
    includeBasicInfo: boolean;
    includeAttachments: boolean;
    includeComments: boolean;
    includeHistory: boolean;
}

interface IPrintDetailsModal {
    visible: boolean;
    onCancel: () => void;
    onPrint: (settings: IPrintDetailsSettings) => void;
    hasFiltersApplied: boolean;
    filteredActivitiesCount: number;
    totalActivitiesCount: number;
}

const PrintDetailsModal: FC<IPrintDetailsModal> = ({
    visible,
    onCancel,
    onPrint,
    hasFiltersApplied,
    filteredActivitiesCount,
    totalActivitiesCount,
}) => {
    const [activitiesScope, setActivitiesScope] = useState<TypePrintActivitiesScope>('filtered');
    const [orientation, setOrientation] = useState<TypePrintOrientation>('portrait');
    const [includeBasicInfo, setIncludeBasicInfo] = useState(true);
    const [includeAttachments, setIncludeAttachments] = useState(true);
    const [includeComments, setIncludeComments] = useState(true);
    const [includeHistory, setIncludeHistory] = useState(true);

    const formatCount = (count: number) => {
        return new Intl.NumberFormat('pt-BR').format(count);
    };

    const hasSelectedData = useMemo(() => {
        return includeBasicInfo || includeAttachments || includeComments || includeHistory;
    }, [includeAttachments, includeBasicInfo, includeComments, includeHistory]);

    useEffect(() => {
        if (!hasFiltersApplied) {
            setActivitiesScope('all');
        }
    }, [hasFiltersApplied]);

    const handlePrint = () => {
        onPrint({
            activitiesScope,
            orientation,
            includeBasicInfo,
            includeAttachments,
            includeComments,
            includeHistory,
        });
    };

    const footerButtons = (
        <FooterButtons>
            <Button
                type="text"
                onClick={onCancel}
                className="buttonCancel"
                height={32}
            >
                Cancelar
            </Button>
            <Button
                type="primary"
                onClick={handlePrint}
                className="buttonPrint"
                height={32}
                disabled={!hasSelectedData}
            >
                <PrinterOutlined className="buttonPrintIcon" />
                Imprimir
            </Button>
        </FooterButtons>
    );

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            width={400}
            title="Imprimir detalhes de atividades"
            newHeader
            footerButtons={footerButtons}
            footerActive={false}
        >
            <Body>
                <Content>
                    <Section>
                        <SectionLabel>
                            <span>Atividades</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <StyledCheckbox>
                            <ActivitiesOption
                                onClick={() => setActivitiesScope('filtered')}
                                disabled={!hasFiltersApplied}
                            >
                                <ActivitiesRadioLabel>
                                    <StyledRadio>
                                        <Radio checked={activitiesScope === 'filtered'} />
                                    </StyledRadio>
                                    <span>Imprimir apenas atividades filtradas</span>
                                    {hasFiltersApplied && (
                                        <ActivitiesTag>{formatCount(filteredActivitiesCount)}</ActivitiesTag>
                                    )}
                                </ActivitiesRadioLabel>
                            </ActivitiesOption>
                            <ActivitiesOption onClick={() => setActivitiesScope('all')}>
                                <ActivitiesRadioLabel>
                                    <StyledRadio>
                                        <Radio checked={activitiesScope === 'all'} />
                                    </StyledRadio>
                                    <span>Imprimir todas as atividades</span>
                                    <ActivitiesTag>{formatCount(totalActivitiesCount)}</ActivitiesTag>
                                </ActivitiesRadioLabel>
                            </ActivitiesOption>
                        </StyledCheckbox>
                    </Section>

                    <Section>
                        <SectionLabel>
                            <span>Orientação do Relatório</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <OrientationOptions>
                            <OrientationOption
                                selected={orientation === 'portrait'}
                                onClick={() => setOrientation('portrait')}
                            >
                                <StyledRadio>
                                    <Radio checked={orientation === 'portrait'} />
                                </StyledRadio>
                                <Icon icon="arquivo" className="orientationIcon" customSize={14} />
                                <OrientationLabel>Retrato</OrientationLabel>
                            </OrientationOption>
                            <OrientationOption
                                selected={orientation === 'landscape'}
                                onClick={() => setOrientation('landscape')}
                            >
                                <StyledRadio>
                                    <Radio checked={orientation === 'landscape'} />
                                </StyledRadio>
                                <Icon
                                    icon="arquivo"
                                    className="orientationIcon orientationIconLandscape"
                                    customSize={14}
                                />
                                <OrientationLabel>Paisagem</OrientationLabel>
                            </OrientationOption>
                        </OrientationOptions>
                    </Section>

                    <Section>
                        <SectionLabel>
                            <span>Dados do Relatório</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <StyledCheckbox>
                            <Checkbox
                                checked={includeBasicInfo}
                                onChange={(event) => setIncludeBasicInfo(event.target.checked)}
                            >
                                Informações básicas
                            </Checkbox>
                            <Checkbox
                                checked={includeAttachments}
                                onChange={(event) => setIncludeAttachments(event.target.checked)}
                            >
                                Anexos
                            </Checkbox>
                            <Checkbox
                                checked={includeComments}
                                onChange={(event) => setIncludeComments(event.target.checked)}
                            >
                                Comentários
                            </Checkbox>
                            <Checkbox
                                checked={includeHistory}
                                onChange={(event) => setIncludeHistory(event.target.checked)}
                            >
                                Histórico
                            </Checkbox>
                        </StyledCheckbox>
                    </Section>
                </Content>
            </Body>
            <FooterDivider />
        </Modal>
    );
};

export default PrintDetailsModal;
